import type {
  BackgroundJobRunGetApiData,
  BackgroundJobRunGetApiResponses,
  BackgroundJobRunListApiData,
  BackgroundJobRunListApiResponses,
  BackgroundJobRunRevokeApiData,
  BackgroundJobRunRevokeApiResponses,
  BackgroundJobRunStatsRecord,
  BackgroundJobScheduleCancelApiData,
  BackgroundJobScheduleCancelApiResponses,
  BackgroundJobScheduleListApiData,
  BackgroundJobScheduleListApiResponses,
  BackgroundJobTaskListApiData,
  BackgroundJobTaskListApiResponses,
  BackgroundJobTaskRunApiData,
  BackgroundJobTaskRunApiResponses,
  ScheduledJobListApiData,
  ScheduledJobListApiResponses,
  ScheduledJobPatchApiData,
  ScheduledJobPatchApiResponses
} from '@navio/nsdk';

import { apiUrl, dataEndpoint, responseEndpoint } from '../defineResource';

/** Distinct values for admin multi-select filters (until in OpenAPI). */
type BackgroundJobRunFilterOptions = {
  taskNames: string[];
  queues: string[];
  workers: string[];
};

/** One filter option with facet count (until in OpenAPI). */
type BackgroundJobRunFilterCount = {
  value: string;
  label: string;
  count: number;
};

/** Facet counts for Task / Status / Queue / Worker filters (until in OpenAPI). */
type BackgroundJobRunFilterCounts = {
  taskNames: BackgroundJobRunFilterCount[];
  statuses: BackgroundJobRunFilterCount[];
  queues: BackgroundJobRunFilterCount[];
  workers: BackgroundJobRunFilterCount[];
};

/**
 * Background job-run + deferred-schedule + recurring scheduled-job REST endpoints.
 * Extra query fields (retries range, duration seconds, multi-select ins) until fully in OpenAPI.
 */
export const backgroundJobUrls = {
  getPaginatedBackgroundJobRuns: dataEndpoint<
    {
      body?: never;
      path?: never;
      query?: NonNullable<BackgroundJobRunListApiData['query']> & {
        retries?: number;
        retriesGte?: number;
        retriesLte?: number;
        durationSecondsGte?: number;
        durationSecondsLte?: number;
        taskNameIn?: string[];
        queueIn?: string[];
        workerIn?: string[];
      };
    },
    BackgroundJobRunListApiResponses
  >({
    getUrl: (queryParams) => apiUrl('core/background-job-runs/', queryParams)
  }),
  getBackgroundJobRunStats: dataEndpoint<
    {
      body?: never;
      path?: never;
      query?: {
        taskNames?: string[] | string;
      };
    },
    { 200: BackgroundJobRunStatsRecord[] }
  >({
    getUrl: (queryParams) => apiUrl('core/background-job-runs/stats/', queryParams)
  }),
  getBackgroundJobRunFilterOptions: dataEndpoint<
    { body?: never; path?: never; query?: never },
    { 200: BackgroundJobRunFilterOptions }
  >({
    getUrl: () => apiUrl('core/background-job-runs/filter-options/')
  }),
  getBackgroundJobRunFilterCounts: dataEndpoint<
    {
      body?: never;
      path?: never;
      query?: NonNullable<BackgroundJobRunListApiData['query']> & {
        taskNameIn?: string[];
        queueIn?: string[];
        workerIn?: string[];
      };
    },
    { 200: BackgroundJobRunFilterCounts }
  >({
    getUrl: (queryParams) => apiUrl('core/background-job-runs/filter-counts/', queryParams)
  }),
  getBackgroundJobRun: dataEndpoint<BackgroundJobRunGetApiData, BackgroundJobRunGetApiResponses>({
    getUrl: ({ taskId }) => apiUrl(`core/background-job-runs/${taskId}/`)
  }),
  postRevokeBackgroundJobRun: responseEndpoint<BackgroundJobRunRevokeApiData, BackgroundJobRunRevokeApiResponses>({
    getUrl: ({ taskId }) => apiUrl(`core/background-job-runs/${taskId}/revoke/`),
    getBody: ({ taskId: _, ...body }) => body
  }),
  getPaginatedBackgroundJobSchedules: dataEndpoint<
    {
      body?: never;
      path?: never;
      query?: NonNullable<BackgroundJobScheduleListApiData['query']> & {
        taskNameIn?: string[];
      };
    },
    BackgroundJobScheduleListApiResponses
  >({
    getUrl: (queryParams) => apiUrl('core/background-job-schedules/', queryParams)
  }),
  postCancelBackgroundJobSchedule: responseEndpoint<
    BackgroundJobScheduleCancelApiData,
    BackgroundJobScheduleCancelApiResponses
  >({
    getUrl: ({ backgroundJobScheduleId }) => apiUrl(`core/background-job-schedules/${backgroundJobScheduleId}/cancel/`),
    getBody: ({ backgroundJobScheduleId: _, ...body }) => body
  }),
  getScheduledJobs: dataEndpoint<ScheduledJobListApiData, ScheduledJobListApiResponses>({
    getUrl: (queryParams) => apiUrl('core/scheduled-jobs/', queryParams)
  }),
  patchScheduledJob: responseEndpoint<ScheduledJobPatchApiData, ScheduledJobPatchApiResponses>({
    getUrl: ({ name }) => apiUrl(`core/scheduled-jobs/${encodeURIComponent(name)}/`),
    getBody: ({ name: _name, ...body }) => body
  }),
  getBackgroundJobTasks: dataEndpoint<BackgroundJobTaskListApiData, BackgroundJobTaskListApiResponses>({
    getUrl: (queryParams) => apiUrl('core/background-job-tasks/', queryParams)
  }),
  postBackgroundJobTaskRun: responseEndpoint<BackgroundJobTaskRunApiData, BackgroundJobTaskRunApiResponses>({
    getUrl: () => apiUrl('core/background-job-tasks/run/'),
    getBody: ({ name, args, sync }) => ({
      name,
      ...(args != null && args.length > 0 ? { args } : {}),
      ...(sync != null ? { sync } : {})
    })
  })
};
